export interface AgendaDueRow {
    touchpointId: string;
    contactId: string;
    contactName: string;
    orgName?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    contactEmailOptedOut: boolean;
    tierName?: string | null;
    tierColor?: string | null;
    kinds: string[];
    intervalCount: number;
    intervalUnit: string;
    lastContactedAt?: string | null;
}

export interface AgendaTouchpoint {
    touchpointId: string;
    kinds: string[];
    intervalCount: number;
    intervalUnit: string;
    lastContactedAt: string | null;
}

export interface AgendaContactGroup {
    contactId: string;
    contactName: string;
    orgName: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    contactEmailOptedOut: boolean;
    tierName: string | null;
    tierColor: string | null;
    touchpoints: AgendaTouchpoint[];
}

export const groupDueTouchpoints = (rows: AgendaDueRow[]): AgendaContactGroup[] => {
    const groups: AgendaContactGroup[] = [];
    const byContact = new Map<string, AgendaContactGroup>();
    for (const row of rows) {
        const existing = byContact.get(row.contactId);
        const group = existing ?? createGroup(row);
        if (!existing) {
            byContact.set(row.contactId, group);
            groups.push(group);
        }
        group.touchpoints.push({
            touchpointId: row.touchpointId,
            kinds: row.kinds,
            intervalCount: row.intervalCount,
            intervalUnit: row.intervalUnit,
            lastContactedAt: row.lastContactedAt ?? null,
        });
    }
    return groups;
};

const createGroup = (row: AgendaDueRow): AgendaContactGroup => ({
    contactId: row.contactId,
    contactName: row.contactName,
    orgName: row.orgName ?? null,
    contactEmail: row.contactEmail ?? null,
    contactPhone: row.contactPhone ?? null,
    contactEmailOptedOut: row.contactEmailOptedOut,
    tierName: row.tierName ?? null,
    tierColor: row.tierColor ?? null,
    touchpoints: [],
});

export type EmailActionState = "send-email" | "add-email" | "email-opted-out";

export const resolveEmailAction = (input: {
    contactEmail: string | null;
    contactEmailOptedOut: boolean;
}): EmailActionState => {
    if (input.contactEmailOptedOut) {
        return "email-opted-out";
    }
    if (input.contactEmail) {
        return "send-email";
    }
    return "add-email";
};

export interface TouchpointActions {
    email: EmailActionState | null;
    addContactPhone: boolean;
    provisionPhoneLine: boolean;
    call: boolean;
    text: boolean;
}

export const resolveTouchpointActions = (input: {
    kinds: string[];
    contactEmail: string | null;
    contactEmailOptedOut: boolean;
    hasContactPhone: boolean;
    hasPhoneLine: boolean;
}): TouchpointActions => {
    const wantsCall = input.kinds.includes("call");
    const wantsText = input.kinds.includes("text");
    const wantsPhone = wantsCall || wantsText;
    const callable = wantsPhone && input.hasContactPhone && input.hasPhoneLine;
    return {
        email: input.kinds.includes("email") ? resolveEmailAction(input) : null,
        addContactPhone: wantsPhone && !input.hasContactPhone,
        provisionPhoneLine: wantsPhone && input.hasContactPhone && !input.hasPhoneLine,
        call: wantsCall && callable,
        text: wantsText && callable,
    };
};

export const agendaNeedsPhoneProvisioning = (
    groups: AgendaContactGroup[],
    hasPhoneLine: boolean,
): boolean => {
    if (hasPhoneLine) {
        return false;
    }
    return groups.some(group =>
        group.touchpoints.some(
            touchpoint =>
                touchpoint.kinds.includes("call") || touchpoint.kinds.includes("text"),
        ),
    );
};
